import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

function PrivacyPolicy() {
  return (
    <div className="py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold text-center mb-8">Privacy Policy</h1>
        
        <Card>
          <CardHeader>
            <CardTitle>Privacy Policy for Hill Bros Brighton Ltd</CardTitle>
            <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
              <p className="mb-3">
                Hill Bros Brighton Ltd ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website www.hill-bros.co.uk or use our services.
              </p>
              <p className="mb-3">
                We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
              <h3 className="text-lg font-medium mb-2">Personal Information You Provide:</h3>
              <ul className="list-disc pl-6 mb-3">
                <li>Name and contact details (email address, phone number, postal address)</li>
                <li>Information provided when requesting quotes or services</li>
                <li>Communication preferences</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3 className="text-lg font-medium mb-2">Information Automatically Collected:</h3>
              <ul className="list-disc pl-6 mb-3">
                <li>IP address and browser information</li>
                <li>Device and connection information</li>
                <li>Page views and site navigation patterns</li>
                <li>Referring website addresses</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">3. Legal Basis for Processing</h2>
              <p className="mb-3">We process your personal data based on:</p>
              <ul className="list-disc pl-6 mb-3">
                <li><strong>Consent:</strong> Where you have given clear consent for us to process your personal data</li>
                <li><strong>Contract:</strong> Where processing is necessary for the performance of a contract with you</li>
                <li><strong>Legal obligation:</strong> Where we need to comply with a legal obligation</li>
                <li><strong>Legitimate interests:</strong> Where it is necessary for our legitimate interests and your interests and fundamental rights do not override those interests</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">4. How We Use Your Information</h2>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-disc pl-6 mb-3">
                <li>Provide and maintain our services</li>
                <li>Process your requests and respond to your inquiries</li>
                <li>Send you quotes and service information</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Protect against fraudulent or illegal activity</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">5. Information Sharing and Disclosure</h2>
              <p className="mb-3">We do not sell, trade, or rent your personal information. We may share your information with:</p>
              <ul className="list-disc pl-6 mb-3">
                <li><strong>Service providers:</strong> Third-party vendors who assist us in operating our website and conducting our business</li>
                <li><strong>Legal requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business transfers:</strong> In connection with any merger, sale of company assets, or acquisition</li>
                <li><strong>With your consent:</strong> When you have given us permission to share your information</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">6. Data Retention</h2>
              <p className="mb-3">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. Typically:
              </p>
              <ul className="list-disc pl-6 mb-3">
                <li>Customer records: 7 years after last service (for tax and warranty purposes)</li>
                <li>Quote inquiries: 2 years if no service provided</li>
                <li>Marketing contacts: Until you unsubscribe</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">7. Your Rights</h2>
              <p className="mb-3">Under UK GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 mb-3">
                <li><strong>Access:</strong> Request copies of your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate personal data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Restrict processing:</strong> Request restriction of processing your personal data</li>
                <li><strong>Data portability:</strong> Request transfer of your data to another organization</li>
                <li><strong>Object:</strong> Object to our processing of your personal data</li>
                <li><strong>Withdraw consent:</strong> Withdraw consent at any time where we rely on consent</li>
              </ul>
              <p className="mb-3">
                To exercise any of these rights, please contact us at info@hill-bros.co.uk or call 01273 326200.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">8. Data Security</h2>
              <p className="mb-3">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">9. Children's Privacy</h2>
              <p className="mb-3">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">10. Third-Party Links</h2>
              <p className="mb-3">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">11. Changes to This Policy</h2>
              <p className="mb-3">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">12. Contact Information</h2>
              <p className="mb-3">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold">Hill Bros Brighton Ltd</p>
                <p>Data Protection Officer</p>
                <p>31 Connaught Road</p>
                <p>Seaford, BN25 2PU</p>
                <p>Email: info@hill-bros.co.uk</p>
                <p>Phone: 01273 326200</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">13. Complaints</h2>
              <p className="mb-3">
                If you wish to make a complaint about how we handle your personal data, please contact us first. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO):
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>Information Commissioner's Office</p>
                <p>Wycliffe House, Water Lane</p>
                <p>Wilmslow, Cheshire, SK9 5AF</p>
                <p>Helpline: 0303 123 1113</p>
                <p>Website: www.ico.org.uk</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default PrivacyPolicy